import type { Block } from "@blocknote/core";

/** Flatten nested BlockNote blocks depth-first. */
export function collectBlocks(blocks: Block[]): Block[] {
  const result: Block[] = [];
  for (const block of blocks) {
    result.push(block);
    const children = (block as Block & { children?: Block[] }).children;
    if (children?.length) result.push(...collectBlocks(children));
  }
  return result;
}

const MEDIA_BLOCK_TYPES = new Set(["image", "video", "audio", "file"]);

function inlineHasText(content: unknown): boolean {
  if (!content) return false;
  if (typeof content === "string") return content.trim().length > 0;
  if (Array.isArray(content)) return content.some(inlineHasText);
  if (typeof content !== "object") return false;

  const record = content as {
    type?: string;
    text?: string;
    url?: string;
    rows?: unknown;
    cells?: unknown;
    content?: unknown;
  };

  if (record.type === "link" || record.type === "image") return true;
  if (typeof record.text === "string" && record.text.trim()) return true;
  if (typeof record.url === "string" && record.url.trim()) return true;
  if (inlineHasText(record.rows) || inlineHasText(record.cells)) return true;
  return inlineHasText(record.content);
}

/** True when the document has text or media — independent of HTML serialization. */
export function editorDocumentHasContent(blocks: Block[]): boolean {
  return collectBlocks(blocks).some((block) => {
    if (MEDIA_BLOCK_TYPES.has(block.type)) {
      return Boolean((block.props as { url?: string } | undefined)?.url);
    }
    if (block.type !== "paragraph") return true;
    return inlineHasText(block.content);
  });
}
